import React from "react";
import { Box, Typography } from "@mui/material";
import Layout from "../Util/Layout";
import Manual_Strat_Logic from "../../Assets/Manual_Strat_Logic.drawio.png"
import experiment1_out_of_sample from "../../Assets/experiment1_out_of_sample.png";
import manual_strategy_vs_benchmark_out_of_sample from "../../Assets/manual_strategy_vs_benchmark_out_of_sample.png"

const MlTradingPage = () => {
  return (
    <Layout>
      <Box p={3}>
        <Typography variant="h3" gutterBottom>
          Machine Learning Trading Agent - Educational Project
        </Typography>

        <Typography variant="h4" color="textSecondary" paragraph>
          My Role: Solo Developer
        </Typography>

        {/* Project Description */}
        <Typography variant="h5" gutterBottom>
          Project Overview
        </Typography>
        <Typography paragraph>
          For this project, I chose 5 technical indicators to inform two separate trading strategies.
          The first strategy is simple and rules-based, using basic indicator thresholds as buy/sell signals.
          The second strategy was developed using a Q-learning approach. For the project, I compared each
          strategy to each other and a benchmark buy-and-hold strategy. Since this was an educational project that
          will continue into future semesters, I am going to leave implementation specifics and parameter values vague.
          If you would like to discuss further details in private, please reach out via the "Contact Me" page with
          a written statement ensuring that you do not intend to take "Machine Learning for Trading" at
          Georgia Tech, and a description of why you would like to discuss.
        </Typography>

        <Typography variant="h5" gutterBottom>
          Manual Strategy
        </Typography>
        <Typography paragraph>
          The manual strategy combines all 5 indicators to make intelligent decisions that look for opportunities where a given stock is priced
          high and overbought, priced low and underbought, has very positive trends, or has very negative trends. If none of those conditions are met on a
          given day, the agent simply holds. I have drawn up a diagram of the logic flow below. Although I can not dosclose the exact indicators I used
          to determine these factors publicly, I would be happy to discuss my selection in private.
        </Typography>
        <Box my={3}>
          <img
            src={Manual_Strat_Logic}
            alt="Manual Strategy Logic Flow"
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        </Box>
        <Typography paragraph>
          Each indicator has some relevant thresholds: "If indicator X is greater than A, that says Z about the holding". To determine the value for A in this case,
          I wrote a Python optimizer that tried different threshold combinations on a vast dataset and reported back with the best results. I have also included a diagram
          of the performance of this strategy below, and it did a lot better than expected. The plot below shows the performance on a redacted holding. On the X-axis is time,
          and on the Y-axis I have normalized price to make viewing a bit easier. The specific holding used could be discussed in private.
        </Typography>
        <Box my={3}>
          <img
            src={manual_strategy_vs_benchmark_out_of_sample}
            alt="Manual Strategy Performance"
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        </Box>
        <Typography variant="h5" gutterBottom>
          ML Learned Strategy
        </Typography>
        <Typography paragraph>
          There are quite a few applicable learning methodologies, and I went with Q-Learning. I did this knowing it might not be the most well-suited to stock market trading
          because the methodology piqued my curiosity, and I have been looking to gain more experience with unfamiliar reinforcement learning techniques. To start, I created a generalized Q-Learner from scratch.
          It was designed to work on any problem that could be represented as a number of states, a number of possible actions, and a reward function. Actions and rewards were easy - I
          just allowed three actions (buy, sell, hold), and the reward is simply the daily return (after adjusting for variables such as commission on trades and market impact). But the states were
          a bit trickier. I ended up sorting each indicator into 6 "buckets" based on the range of values reported for that indicator in the training data. If an indicator could go from 0-11, the
          buckets would be 0-1, 2-3, 4-5, 6-7, 8-9, 10-11. That way, a given state looks something like this [3, 0, 5, 4, 1], where each value represents the bucket ID for a different indicator.
          The final step was mapping each possible state to a unique integer ID (since that's what my Q-Learner expected). I did this by treating each of the 5 numbers as a single digit in a base-6 integer,
          using this equation for a given state [a, b, c, d, e]: new_state = (a*6^4)+(b*6^3)+(c*6^2)+(d*6)+e. With the actions, states, and rewards all figured out, I could get to training!
        </Typography>
        <Typography paragraph>
          Q-Learning has a few hyperparameters to select, such as learning rate, random action rate, and more. To select these, I used an external optimizer just like I did to select the thresholds
          for the manual strategy. That script took almost 2 entire days to run, but it resulted in performance far better than the parameters I picked just based on my own intuition:
        </Typography>
        <Box my={3}>
          <img
            src={experiment1_out_of_sample}
            alt="Experiment 1 out of sample"
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        </Box>
        <Typography paragraph>
          As you can see, it is pretty neck-and-neck with the manually created strategy in this particular instance. To be honest, on most holdings I tried, the manual strategy came out on top
          by a thin margin. This demonstrates not only the inconsistency of both the market and my learner strategy, but also the need for significant amounts of high quality training data.
        </Typography>

        <Typography variant="h5" gutterBottom>
          Other Work
        </Typography>
        <Typography paragraph>
          There were a lot of other tasks I had to implement to bring this all together. I had to create a market simulator to run this all on, develop external scripts to test everything in various scenarios,
          create code to generate all charts seen on this page (and many more not included here), and I run multiple experiments specific to the assignment (measuring how the agent behaved with different settings,
          different levels of market impact, etc). If you would like to hear about everything else that went into this, don't hesitate to reach out!
        </Typography>
        <Typography variant="h5" gutterBottom>
          What I Learned
        </Typography>
        <Typography paragraph>
          I learned so so much while completing this project. A few highlights were doing research on ~20 indicators to carefully select the 5 I wanted to use, learning Q-Learning concepts from 0 understanding
          to the ability to fully implement them from scratch, and gaining a deep understanding of NumPy and Pandas for data manipulation. But my biggest takeaway was how hard agentic trading is to get right.
          Even after 50+ hours putting all of this together, my Q-Learning approach often fell short of the manually created strategy, which was not even that sophisticated. I view this as a challenge, and I am
          excited to try out new ideas when it comes to applying machine learning to trading.
        </Typography>
      </Box>
    </Layout>
  );
};

export default MlTradingPage;
